/** @jsxImportSource @emotion/react */
"use client";

import type { Interpolation } from "@emotion/react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "@i18n/react";
import { useConsent } from "@packages/consent";
import type { PasswordAuthFlowForm } from "@packages/sdk";
import {
  passwordAuthFlowSchema,
  preexistingPasswordAuthFlowSchema,
  useRequestAuth,
} from "@packages/sdk";
import { useRef, useState } from "react";
import { useForm } from "react-hook-form";

import { spacing } from "../../../../global/emotion";
import { useIsDesktopViewport } from "../../../hooks/useIsDesktopViewport";
import { useValidator } from "../../../hooks/useValidator.emotion";
import { cssMediaQueryMinWidthTablet } from "../../../lib/constants/emotionMediaQueries.constants";
import { Button } from "../../_base/_core/Button/Button.emotion";
import { IconButton } from "../../_base/_core/IconButton/IconButton.emotion";
import { Text } from "../../_base/_core/Text/Text.emotion";
import { EyeIcon } from "../../_base/icons/Icon/GeneralIcons";
import { useAlert } from "../../_base/modals/Dialog/Dialog.emotion";
import { FormField } from "../forms/wrapper/FormField.emotion";
import { AuthError } from "./AuthError.emotion";
import { EmailHeader } from "./AuthFlowHeader.emotion";
import { AuthForm } from "./AuthForm.emotion";
import { AuthSubmitButton } from "./AuthSubmitButton.emotion";
import { AuthTextInput } from "./AuthTextInput.emotion";
import type { AuthFlowProps } from "./AuthTypes.emotion";

const buttonsStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  flexDirection: "column-reverse",
  [cssMediaQueryMinWidthTablet]: {
    flexDirection: "row",
    justifyContent: "space-between",
  },
  gap: spacing.ms,
  justifyContent: "flex-end",
};
const justOneStyle: Interpolation = {
  justifyContent: "flex-end",
};
const inputFormFieldStyle: Interpolation = {
  width: spacing.full,
};

export const PasswordVisibilityButton = ({
  onClick,
  visible,
}: {
  onClick: () => void;
  visible: boolean;
}) => {
  return (
    <IconButton
      icon={visible ? <EyeIcon.Off /> : <EyeIcon.On />}
      onClick={onClick}
      type="button"
      size="m"
      variant="transparentPrimary"
    />
  );
};

export const PasswordAuthFlowWithSignup = ({
  onAuth,
  setFlow,
  state,
  utm,
  onFormSubmit,
  onCaptchaVerify: reportVerification,
  onError,
  formDisabled = false,
  hideHeader,
  className = "",
}: AuthFlowProps) => {
  const t = useTranslations();
  const { consent } = useConsent();
  const alert = useAlert();
  const { mutationRegister, mutationLogin, mutationForgotPassword } =
    useRequestAuth();
  const [visible, setVisible] = useState(false);
  const isDesktop = useIsDesktopViewport();
  const captchaRef = useRef(null);
  const { email, exists } = state;
  const { resolver, errors, errorsCSS, fieldsCSS, inputProps } =
    useValidator<PasswordAuthFlowForm>(
      zodResolver(
        exists ? preexistingPasswordAuthFlowSchema : passwordAuthFlowSchema(t),
      ),
    );
  const resolveRegister = useRef<(() => void) | null>(null);

  const form = useForm<PasswordAuthFlowForm>({ resolver });

  const onSubmit = async ({ password }: PasswordAuthFlowForm) => {
    onFormSubmit?.({ email, ...utm });
    try {
      // If email registration, check captcha value
      if (!exists) {
        captchaRef.current.execute();
        return new Promise<void>(
          (resolve) => (resolveRegister.current = resolve),
        );
      } else {
        const data = await mutationLogin.mutateAsync({ email, password });
        await onAuth?.({ data });
      }
    } catch (error) {
      onError?.(error);
      alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  const onCaptchaVerify = async (captcha: string) => {
    const { password } = form.getValues();

    reportVerification?.({ email, ...utm });

    try {
      const data = await mutationRegister.mutateAsync({
        email,
        password,
        captcha,
        ...utm,
        consent,
      });
      await onAuth?.({ data });
      resolveRegister.current?.();
    } catch (error) {
      onError?.(error);
      alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
      resolveRegister.current?.();
    }
  };

  const onCaptchaError = () => {
    onError?.(null);
    alert.show({
      title: t("general_word_error"),
      description: t("error_registration_failure"),
    });
  };

  const handlePasswordReset = async () => {
    try {
      await mutationForgotPassword.mutateAsync({ email });

      alert.show({
        title: t("general_word_success_exclamation"),
        description: t("dialog_reset_password_email_sent_message", {
          0: email,
        }),
      });
    } catch (error) {
      alert.show({
        title: t("general_word_error"),
        description: error.message,
      });
    }
  };

  // if state isn't loaded properly yet, don't show broken stuff
  if (!email) return null;

  return (
    <AuthForm<PasswordAuthFlowForm>
      form={form}
      onSubmit={onSubmit}
      className={className}
      footer={(_, disabled) => (
        <div key={"buttons"} css={[buttonsStyle, !exists && justOneStyle]}>
          {exists && (
            <Button
              isFullWidth={!isDesktop}
              size="m"
              variant="transparentPrimary"
              type="button"
              onClick={() => handlePasswordReset()}
            >
              {t("password_input_help_option1")}
            </Button>
          )}
          <AuthSubmitButton disabled={disabled || formDisabled}>
            {t(exists ? "login_title" : "registration_button_text")}
          </AuthSubmitButton>
        </div>
      )}
    >
      {(form) => (
        <>
          {!hideHeader && (
            <EmailHeader
              title={t(exists ? "login_title" : "auth_sign_up_title")}
              onBack={() => setFlow("email")}
            />
          )}
          <Text type="headline" size="m" as="h2">
            {t(
              exists
                ? "password_input_headline_existing"
                : "auth_welcome_to_new_account",
            )}
          </Text>
          <input type="hidden" name="email" value={email} />
          <FormField
            control={form.control}
            name="password"
            showErrorMessage={true}
            defaultValue={""}
            render={({ field }) => (
              <div>
                <AuthTextInput
                  type={visible ? "text" : "password"}
                  required
                  autoFocus
                  autoCorrect="off"
                  autoComplete={exists ? "current-password" : "new-password"}
                  placeholder={t(
                    exists
                      ? "auth_enter_login_password"
                      : "auth_create_your_password",
                  )}
                  button={
                    <PasswordVisibilityButton
                      visible={visible}
                      onClick={() => setVisible(!visible)}
                    />
                  }
                  disabled={formDisabled}
                  {...inputProps}
                  css={fieldsCSS.password}
                  {...field}
                />
                <AuthError css={errorsCSS.password}>
                  {errors?.password?.message ? t(errors.password.message) : ""}
                </AuthError>

                <HCaptcha
                  onError={onCaptchaError}
                  onVerify={onCaptchaVerify}
                  ref={captchaRef}
                  sitekey={process.env.NEXT_PUBLIC_HCAPTCHA_KEY}
                  size="invisible"
                />
              </div>
            )}
            css={inputFormFieldStyle}
          />
        </>
      )}
    </AuthForm>
  );
};

PasswordAuthFlowWithSignup.displayName = "PasswordAuthFlowWithSignup";
